import { useEffect, useState } from "react";
import type { User } from "./types";

const KEY = "gitfit:progress";

export interface ProgressState {
  // courseId -> set of completed stepIds
  steps: Record<string, string[]>;
  // courseId -> quiz score (0..1)
  quiz: Record<string, number>;
  // courseId -> whether the learner explicitly marked the course complete.
  // This — not step coverage — is what unlocks the next course in the sequence.
  completed: Record<string, boolean>;
  // courseId -> last step index the learner was on (resume point). Client-only;
  // not part of the server-synced payload.
  cursor?: Record<string, number>;
  // The course the learner most recently opened. Drives the dashboard "Resume"
  // card. Client-only.
  lastCourseId?: string;
}

const EMPTY: ProgressState = { steps: {}, quiz: {}, completed: {}, cursor: {} };

function read(): ProgressState {
  if (typeof window === "undefined") return EMPTY;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as ProgressState;
    // Normalize older payloads written before `completed` existed.
    return { ...parsed, completed: parsed.completed ?? {} };
  } catch {
    return EMPTY;
  }
}

function write(p: ProgressState) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    // ignore
  }
}

type Listener = (p: ProgressState) => void;
const listeners = new Set<Listener>();
function notify(p: ProgressState) {
  listeners.forEach((l) => l(p));
}

// ----------------------------------------------------------------------------
// Server sync
//
// localStorage stays the instant-feedback primary; the server (SQLite) holds
// the durable, cross-device record. Reconciliation is MONOTONIC — union of
// completed steps, max of quiz scores per course — so logging in on a second
// device can never erase progress, and writes are safe to replay. Pushes are
// debounced and gated on having an authenticated session; offline writes sit
// in localStorage and flush on the next successful push (write, reconnect, or
// re-hydrate).
// ----------------------------------------------------------------------------

function mergeStates(a: ProgressState, b: ProgressState): ProgressState {
  const steps: Record<string, string[]> = {};
  for (const id of new Set([...Object.keys(a.steps), ...Object.keys(b.steps)])) {
    steps[id] = [...new Set([...(a.steps[id] ?? []), ...(b.steps[id] ?? [])])];
  }
  const quiz: Record<string, number> = {};
  for (const id of new Set([...Object.keys(a.quiz), ...Object.keys(b.quiz)])) {
    quiz[id] = Math.max(a.quiz[id] ?? 0, b.quiz[id] ?? 0);
  }
  // Completion is monotonic — once either side has it, it stays.
  const completed: Record<string, boolean> = {};
  for (const id of new Set([
    ...Object.keys(a.completed ?? {}),
    ...Object.keys(b.completed ?? {}),
  ])) {
    if (a.completed?.[id] || b.completed?.[id]) completed[id] = true;
  }
  // cursor / lastCourseId are client-only and never come back from the server,
  // so the local side (always `a` at the call sites) is authoritative. Take the
  // furthest step per course so reconciliation can't pull a learner backwards.
  const cursor: Record<string, number> = {};
  for (const id of new Set([
    ...Object.keys(a.cursor ?? {}),
    ...Object.keys(b.cursor ?? {}),
  ])) {
    cursor[id] = Math.max(a.cursor?.[id] ?? 0, b.cursor?.[id] ?? 0);
  }
  return {
    steps,
    quiz,
    completed,
    cursor,
    lastCourseId: a.lastCourseId ?? b.lastCourseId,
  };
}

// Gated on a confirmed session — flipped by hydrate(). Avoids hammering the
// API with 401s while logged out.
let pushEnabled = false;
let syncing = false;
let pendingPush = false;
let pushTimer: ReturnType<typeof setTimeout> | null = null;

async function pushToServer() {
  if (typeof window === "undefined" || !pushEnabled) return;
  if (syncing) {
    pendingPush = true;
    return;
  }
  syncing = true;
  try {
    const res = await fetch("/api/progress", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify(read()),
    });
    if (res.ok) {
      const server = (await res.json()) as ProgressState;
      const merged = mergeStates(read(), server);
      write(merged);
      notify(merged);
    } else if (res.status === 401) {
      pushEnabled = false; // session gone — stop pushing
    }
  } catch {
    pendingPush = true; // offline — retry later
  } finally {
    syncing = false;
    if (pendingPush) {
      pendingPush = false;
      schedulePush();
    }
  }
}

function schedulePush() {
  if (typeof window === "undefined" || !pushEnabled || pushTimer) return;
  // Debounce bursts of step completions into one request.
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void pushToServer();
  }, 400);
}

async function hydrateFromServer() {
  if (typeof window === "undefined") return;
  try {
    const res = await fetch("/api/progress", { credentials: "include" });
    if (!res.ok) {
      pushEnabled = false; // 401 = anonymous; stay local-only
      return;
    }
    const server = (await res.json()) as ProgressState;
    const merged = mergeStates(read(), server);
    write(merged);
    notify(merged);
    pushEnabled = true;
    // Push the merged result back so the server gains anything only local had.
    void pushToServer();
  } catch {
    // Offline — local stands; a later write/online event will reconcile.
  }
}

export function useProgress() {
  const [state, setState] = useState<ProgressState>(() => read());

  useEffect(() => {
    const l: Listener = (p) => setState(p);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);

  return state;
}

/**
 * Reconcile localStorage with the server for the signed-in learner. Call once
 * at the router level. Re-hydrates when the user changes (login/logout) and
 * flushes any queued local writes when the browser comes back online.
 */
export function useProgressSync(user: User | null) {
  useEffect(() => {
    if (!user) {
      pushEnabled = false;
      return;
    }
    void hydrateFromServer();

    const onOnline = () => {
      if (pushEnabled) void pushToServer();
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [user?.id]);
}

export function setStepDone(courseId: string, stepId: string) {
  const p = read();
  const list = new Set(p.steps[courseId] || []);
  list.add(stepId);
  const next: ProgressState = {
    ...p,
    steps: { ...p.steps, [courseId]: [...list] },
  };
  write(next);
  notify(next);
  schedulePush();
}

export function setQuizScore(courseId: string, score: number) {
  const p = read();
  // Monotonic locally too — never let a retake lower the recorded score.
  const best = Math.max(p.quiz[courseId] ?? 0, score);
  const next = { ...p, quiz: { ...p.quiz, [courseId]: best } };
  write(next);
  notify(next);
  schedulePush();
}

/**
 * Mark a course as explicitly complete (the learner clicked "Mark course as
 * complete" on the final step). Monotonic: never un-completes. This is what
 * unlocks the next course in the sequence and lights up the admin view.
 */
export function setCourseComplete(courseId: string) {
  const p = read();
  if (p.completed?.[courseId]) return;
  const next: ProgressState = {
    ...p,
    completed: { ...(p.completed ?? {}), [courseId]: true },
  };
  write(next);
  notify(next);
  schedulePush();
}

export function isStepDone(
  state: ProgressState,
  courseId: string,
  stepId: string,
): boolean {
  return (state.steps[courseId] || []).includes(stepId);
}

/**
 * Record the step the learner is currently on for a course, and mark this as the
 * course they were last on. Drives in-course resume and the dashboard "Resume"
 * card. localStorage-only (not synced), but stored in ProgressState so it rides
 * the same write/notify path and live-updates subscribers.
 */
export function setCursor(courseId: string, stepIdx: number) {
  const p = read();
  if (p.cursor?.[courseId] === stepIdx && p.lastCourseId === courseId) return;
  const next: ProgressState = {
    ...p,
    cursor: { ...p.cursor, [courseId]: stepIdx },
    lastCourseId: courseId,
  };
  write(next);
  notify(next);
}

/** The step index to resume a course on (0 if never started). */
export function getCursor(state: ProgressState, courseId: string): number {
  return state.cursor?.[courseId] ?? 0;
}

/** Read the resume step for a course straight from localStorage (no subscription). */
export function readCursor(courseId: string): number {
  return read().cursor?.[courseId] ?? 0;
}

/** The course the learner most recently opened, if any. */
export function getLastCourseId(state: ProgressState): string | undefined {
  return state.lastCourseId;
}
